import { FALLBACK_LANGUAGE } from '@frontends/util-i18n';
import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useLayoutEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { axiosIb, axiosPrivate } from '@/lib/axios';
import i18n from '@/lib/i18n';
import useAuth from './useAuth';

let localScopedAccessToken: string | null = null;
let refreshPromiseAccessToken: Promise<string | null> | null = null;

let areInterceptorsRegistered = false;
let publishToken: ((token?: string) => void) | null = null;

export const setAxiosPrivateAccessToken = (accessToken?: string | null) => {
  localScopedAccessToken = accessToken ?? null;

  if (localScopedAccessToken) {
    axiosPrivate.defaults.headers.common['Authorization'] = `Bearer ${localScopedAccessToken}`;
  } else {
    delete axiosPrivate.defaults.headers.common['Authorization'];
  }
};

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const transformAxiosErrorToGenericError = (axiosError: AxiosError): any => {
  const { message, response, status: statusCode, stack: axiosStackError } = axiosError;
  const { data } = response ?? ({} as any);
  const { detail, code, context, stack } = data ?? ({} as any);

  return {
    detail: detail || message,
    code: code || statusCode,
    context: context || {},
    stack: stack || axiosStackError,
  };
};

const getFreshAccessToken = async (): Promise<string | null> => {
  if (!refreshPromiseAccessToken) {
    refreshPromiseAccessToken = (async () => {
      try {
        const response = await axiosPrivate.put<{ access_token: string | null }>(`/auth/refresh-token`);
        refreshPromiseAccessToken = null;

        return response.data.access_token ?? null;
      } catch {
        refreshPromiseAccessToken = null;

        return null;
      }
    })();
  }

  return refreshPromiseAccessToken;
};

const attachPrivateInterceptors = (instance: AxiosInstance) => {
  instance.interceptors.request.use((req: InternalAxiosRequestConfig) => {
    req.headers = req.headers ?? {};
    (req.headers as any)['X-Trace-ID'] = uuidv4();
    (req.headers as any)['Accept-Language'] = i18n.language || FALLBACK_LANGUAGE;

    if (localScopedAccessToken) {
      (req.headers as any)['Authorization'] = `Bearer ${localScopedAccessToken}`;
    }

    return req;
  });

  instance.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      const original = error.config as RetryableConfig | undefined;
      const status = error.response?.status;

      if (!original) return Promise.reject(transformAxiosErrorToGenericError(error));

      original.headers = original.headers ?? {};

      const isAuthUrl = original.url?.includes('/auth/refresh-token');
      const shouldTryRefresh = (status === 401 || status === 498) && !isAuthUrl;

      if (!shouldTryRefresh || original._retry) {
        return Promise.reject(transformAxiosErrorToGenericError(error));
      }

      original._retry = true;

      try {
        const freshAccessToken = await getFreshAccessToken();

        if (freshAccessToken) {
          setAxiosPrivateAccessToken(freshAccessToken);
          publishToken?.(freshAccessToken);
          original.headers['Authorization'] = `Bearer ${freshAccessToken}`;
          original.headers['X-Trace-ID'] = uuidv4();

          // Retry on the instance that failed — axiosIb requests must not be replayed against id-backend
          return instance(original);
        }
      } catch {}

      setAxiosPrivateAccessToken(null);
      publishToken?.(undefined);

      return Promise.reject(transformAxiosErrorToGenericError(error));
    },
  );
};

const registerInterceptors = () => {
  if (areInterceptorsRegistered) {
    return;
  }

  areInterceptorsRegistered = true;

  attachPrivateInterceptors(axiosPrivate);
  attachPrivateInterceptors(axiosIb);
};

const useAxiosInterceptors = () => {
  const { token, setToken } = useAuth();
  setAxiosPrivateAccessToken(token);
  publishToken = setToken;

  useLayoutEffect(() => {
    registerInterceptors();
  }, []);

  return axiosPrivate;
};

export default useAxiosInterceptors;
